/**
 * Products Slider Module
 * 
 * Gère les sliders de produits avec navigation manuelle et pagination
 * Utilise GSAP pour les animations
 */

import { gsap } from 'gsap';

/**
 * Classe pour gérer un slider de produits
 */
class ProductsSlider {
    constructor(container) {
        this.container = container;
        this.wrapper = container.querySelector('.swiper-wrapper');
        this.slides = container.querySelectorAll('.swiper-slide');
        
        // Les boutons sont maintenant dans le même niveau parent que le container
        const sliderParent = container.closest('.flex.items-center') || container.parentElement;
        this.prevBtn = sliderParent.querySelector('.products-slider-prev');
        this.nextBtn = sliderParent.querySelector('.products-slider-next');
        this.pagination = sliderParent.parentElement.querySelector('.products-slider-pagination');
        
        this.currentIndex = 0;
        this.slidesPerView = this.getSlidesPerView();
        this.totalSlides = this.slides.length;
        this.maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);
        
        this.init();
    }
    
    /**
     * Initialise le slider
     */
    init() {
        this.setupSlider();
        this.createPagination();
        this.bindEvents();
        this.updateNavigation();
        
        // Animation d'entrée pour les slides
        gsap.from(this.slides, {
            duration: 0.6,
            y: 30,
            opacity: 0,
            stagger: 0.1,
            ease: "power2.out"
        });
    }
    
    /**
     * Configure le slider (positionnement initial)
     */
    setupSlider() {
        // Calculer la largeur exacte des slides en tenant compte des marges
        const slideWidth = 100 / this.slidesPerView;
        
        // S'assurer que le wrapper fait exactement la largeur de son container
        this.wrapper.style.width = '100%';
        this.wrapper.style.boxSizing = 'border-box';
        
        this.slides.forEach((slide, index) => {
            // Chaque slide prend exactement sa part de l'espace disponible
            // La largeur inclut déjà les marges internes (px-3)
            slide.style.flex = `0 0 ${slideWidth}%`;
            slide.style.maxWidth = `${slideWidth}%`;
            slide.style.minWidth = `${slideWidth}%`;
            slide.style.boxSizing = 'border-box';
        });
        
        // Position initiale (recalculée avec les marges)
        this.updateSlidePosition();
    }
    
    /**
     * Détermine le nombre de slides visibles selon la taille d'écran
     * Responsive adapté aux breakpoints Tailwind
     */
    getSlidesPerView() {
        const width = window.innerWidth;
        
        if (width < 640) return 1;   // Mobile (sm): 1 produit
        if (width < 1024) return 2;  // Tablette (md/lg): 2 produits  
        if (width < 1536) return 3;  // Desktop (xl): 3 produits
        return 4; // Grande résolution (2xl): 4 produits
    }
    
    /**
     * Crée la pagination
     */
    createPagination() {
        if (!this.pagination) return;
        
        this.pagination.innerHTML = '';
        const totalPages = this.maxIndex + 1;
        
        for (let i = 0; i < totalPages; i++) {
            const dot = document.createElement('button');
            dot.className = 'w-3 h-3 bg-gray-300 rounded-full cursor-pointer transition-all duration-300 hover:bg-orange-400';
            dot.dataset.index = i;
            dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
            
            if (i === this.currentIndex) {
                dot.classList.remove('bg-gray-300', 'w-3');
                dot.classList.add('!bg-orange-500', '!w-6');
            }
            
            dot.addEventListener('click', () => this.goToSlide(i));
            this.pagination.appendChild(dot);
        }
    }
    
    /**
     * Met à jour la position des slides
     */
    updateSlidePosition() {
        const slideWidth = 100 / this.slidesPerView;
        
        // Calculer le padding des slides (px-3 = 0.75rem de chaque côté = 1.5rem total)
        // On convertit en % par rapport à la largeur du container
        const containerWidth = this.container.offsetWidth;
        const slidePaddingRem = 1.5; // px-3 = 0.75rem × 2
        const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const slidePaddingPx = slidePaddingRem * remToPx;
        const slidePaddingPercent = (slidePaddingPx / containerWidth) * 100;
        
        // Calcul du translate en tenant compte des marges des slides
        const effectiveSlideWidth = slideWidth + (slidePaddingPercent / this.slidesPerView);
        const translateX = -(this.currentIndex * effectiveSlideWidth);
        
        gsap.to(this.wrapper, {
            duration: 0.6,
            x: `${translateX}%`,
            ease: "power2.out"
        });
    }
    
    /**
     * Met à jour l'état de la navigation
     */
    updateNavigation() {
        // Boutons de navigation
        if (this.prevBtn) {
            this.prevBtn.disabled = this.currentIndex === 0;
        }
        
        if (this.nextBtn) {
            this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
        }
        
        // Pagination
        if (this.pagination) {
            const dots = this.pagination.querySelectorAll('button');
            dots.forEach((dot, index) => {
                if (index === this.currentIndex) {
                    dot.classList.remove('bg-gray-300', 'w-3');
                    dot.classList.add('!bg-orange-500', '!w-6');
                } else {
                    dot.classList.remove('!bg-orange-500', '!w-6');
                    dot.classList.add('bg-gray-300', 'w-3');
                }
            });
        }
    }
    
    /**
     * Va au slide suivant
     */
    nextSlide() {
        if (this.currentIndex < this.maxIndex) {
            this.currentIndex++;
            this.updateSlidePosition();
            this.updateNavigation();
            
            // Animation des boutons
            gsap.to(this.nextBtn, { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 });
        }
    }
    
    /**
     * Va au slide précédent
     */
    prevSlide() {
        if (this.currentIndex > 0) {
            this.currentIndex--;
            this.updateSlidePosition();
            this.updateNavigation();
            
            // Animation des boutons
            gsap.to(this.prevBtn, { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 });
        }
    }
    
    /**
     * Va à un slide spécifique
     */
    goToSlide(index) {
        if (index >= 0 && index <= this.maxIndex && index !== this.currentIndex) {
            this.currentIndex = index;
            this.updateSlidePosition();
            this.updateNavigation();
        }
    }
    
    /**
     * Lie les événements
     */
    bindEvents() {
        // Navigation par boutons
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.prevSlide());
        }
        
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.nextSlide());
        }
        
        // Navigation par clavier
        document.addEventListener('keydown', (e) => {
            if (this.container.closest('.products-slider').matches(':hover')) {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    this.prevSlide();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    this.nextSlide();
                }
            }
        });
        
        // Redimensionnement de la fenêtre
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.handleResize();
            }, 250);
        });
    }
    
    /**
     * Gère le redimensionnement
     */
    handleResize() {
        const newSlidesPerView = this.getSlidesPerView();
        
        if (newSlidesPerView !== this.slidesPerView) {
            this.slidesPerView = newSlidesPerView;
            this.maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);
            
            // Ajuster l'index si nécessaire
            if (this.currentIndex > this.maxIndex) {
                this.currentIndex = this.maxIndex;
            }
            
            this.setupSlider();
            this.createPagination();
            this.updateNavigation();
        } else {
            // Même si le nombre de slides ne change pas, recalculer les positions
            // car les dimensions du container peuvent avoir changé
            this.updateSlidePosition();
        }
    }
}

/**
 * Initialise tous les sliders de produits sur la page
 */
export function initProductsSliders() {
    const sliders = document.querySelectorAll('.products-slider');
    
    sliders.forEach((slider, index) => {
        // Ajouter un attribut pour identifier le slider
        slider.dataset.sliderId = `products-slider-${index}`;
        
        // Récupérer les options depuis les attributs data du slider lui-même
        const slidesPerView = slider.dataset.slidesPerView || '4';
        
        // Initialiser le slider
        new ProductsSlider(slider);
    });

}
